import { PreferencesContext } from '../../../App';
import { ConfigurationProps } from '../../Configuration';
import { v4 as uuidv4 } from 'uuid';
import { useContainerSize, ApiFactory } from '../../shared/shared';
import ContentItem, {
	ButtonProps,
	ContentFooter,
	ContentItemProps,
	useConfirm,
} from '../ContentItem';
import { SummaryScroll, ItemIterator, duplicateConfiguration } from '../Shared';
import {
	getModelData,
	getFractionalCoordinates,
	ModelBox,
	getCollidersFromBoxes,
	detectAnyCollision,
	areManyBoxesWithinBounds,
} from '../layout_utils';
import {
	Pallet_t,
	PlaneDimensions,
	Layout_t,
	Stack as StackObject,
	getBoxPositionsBoundingBoxCenter,
	Layer_t,
	MultiPickType,
	rotateBox90Degrees,
	Box_t,
} from 'blackbox';
import { useState, useMemo, useContext } from 'react';
import { useDebounce } from './utilities/hooks';
import { MultiPickData, SelectMode } from './utilities/types';
import { EditorHeaderProps, EditorHeader } from './components/RightSidebar';
import { EditorBarProps, EditorBar } from './components/Toolbar';
import { LayoutCellProps, LayoutCell } from './components/LayoutCell';
import { LayoutModelProps, LayoutModel } from './canvas/LayoutModel';
import { AutoLayout, AutoLayoutProps } from './canvas/AutoLayout';

const LOG_LABEL_SPINS: boolean = false;
const DEFAULT_SPACING_MM: number = 10;

function defaultLayoutName(pIndex: number) {
	return 'Layout ' + String(pIndex);
}

function defaultLayout(index: number, pGap: number): Layout_t {
	return {
		name: defaultLayoutName(index),
		id: String(uuidv4()),
		box_id: '',
		boxPositions: [],
		gapDistance: pGap,
		overhang: 0,
	};
}

export default function Layout({
	instructionNumber,
	allBoxes,
	allPallets,
	setPallets,
	unitsSystem,
	triggerKeyboard,
	triggerSelect,
	handleNext,
	handleBack,
}: ConfigurationProps) {
	let layoutCount = 0;
	const { ref: lContainerRef, rect: lContainerRect } =
			useContainerSize<HTMLDivElement>(),
		lSize = useMemo(() => {
			if (!lContainerRect) return 0;
			const size = Math.min(lContainerRect.width, lContainerRect.height);
			return Math.max(size, 0);
		}, [lContainerRect]);

	const { preferences, setPreferences } = useContext(PreferencesContext) as any;
	const lDefaultGapDistance =
		preferences.cached_gap_distance || DEFAULT_SPACING_MM;
	const [selectedBoxes, setSelectedBoxes] = useState<number[]>([]);
	const [selectMode, setSelectMode] = useState<number>(SelectMode.Single);
	const [currentBoxIndex, setCurrentBoxIndex] = useState<number>(0);
	const [currentPalletIndex, setCurrentPalletIndex] = useState<number>(0);
	const [currentLayoutIndex, setCurrentLayoutIndex] = useState<number>(0);
	const [summaryScreen, setSummaryScreen] = useState<boolean>(true);
	const [showAuto, setShowAuto] = useState<boolean>(false);
	const [modelBoxes, setModelBoxes] = useState<ModelBox[]>([]);
	const [editingLayout, setEditingLayout] = useState<Layout_t>(
		defaultLayout(layoutCount + 1, lDefaultGapDistance)
	);
	const [moveBoxes, setMoveBoxes] = useState<boolean>(false);
	const [snapStrength, setSnapStrength] = useState<number>(20);

	interface _LayoutPair {
		PalletIndex: number;
		LayoutIndex: number;
	}

	function toggleMoveBoxes() {
		setMoveBoxes(!moveBoxes);
	}

	function getEffectiveBoxDimensions(
		b: ModelBox,
		box: Box_t
	): { width: number; length: number } {
		const SAFETY_FACTOR = 0.01;
		let baseW = box.dimensions.width,
			baseL = box.dimensions.length;

		baseW += baseW * SAFETY_FACTOR;
		baseL += baseL * SAFETY_FACTOR;

		const times = b.multipick ? b.multipick + 1 : 1;

		let w: number;
		let l: number;

		const multipickOnWidth = b.rotated
			? !b.multipickAlongWidth
			: b.multipickAlongWidth;

		if (b.rotated) {
			w = baseL;
			l = baseW;
		} else {
			w = baseW;
			l = baseL;
		}
		if (multipickOnWidth) {
			w *= times;
		} else {
			l *= times;
		}
		return { width: w, length: l };
	}

	const isNewLayout = useMemo(() => {
		// Check if the editing layout exists in the current pallet's layouts
		const currentLayouts = allPallets[currentPalletIndex]?.Layouts || [];
		return !currentLayouts.some(layout => layout.id === editingLayout.id);
	}, [allPallets, currentPalletIndex, editingLayout]);

	function layoutNameUnique() {
		for (let i = 0; i < allPallets.length; i++) {
			const pallet = allPallets[i];
			for (let l = 0; l < pallet.Layouts.length; l++) {
				if (i !== currentPalletIndex || l !== currentLayoutIndex) {
					if (
						pallet.Layouts[l].name.toLowerCase() === editingLayout.name.toLowerCase()
					) {
						return false;
					}
				}
			}
		}
		return true;
	}
	const [lSnapEnabled, lSetSnapEnabled] = useState<boolean>(true),
		lToggleSnap = function () {
			lSetSnapEnabled(!lSnapEnabled);
		},
		lAllLayoutPairs = useMemo(
			function (): _LayoutPair[] {
				return allPallets
					.map(function (pPallet: Pallet_t, pPalletIndex: number) {
						return pPallet.Layouts.map(function (
							pLayout: Layout_t,
							pLayoutIndex: number
						): _LayoutPair {
							return {
								PalletIndex: pPalletIndex,
								LayoutIndex: pLayoutIndex,
							};
						});
					})
					.flat();
			},
			[allPallets]
		),
		[lTriggerConfirm, lConfirmProps] = useConfirm([summaryScreen]),
		lGapDistance = editingLayout.gapDistance,
		lSetGapDistance = function (pNewGap: number) {
			if (pNewGap < 0) pNewGap = 0;
			const lCp = { ...editingLayout };
			lCp.gapDistance = pNewGap;
			setEditingLayout(lCp);
			setPreferences({ ...preferences, cached_gap_distance: pNewGap });
		},
		LeftButton: ButtonProps = {
			name: summaryScreen ? '' : 'Back',
			action: () => {
				if (summaryScreen) {
					handleBack();
					return;
				}
				setShowAuto(false);
				setSummaryScreen(true);
			},
			enabled: !summaryScreen,
		},
		selectBox = function (i: number) {
			if (selectMode === SelectMode.Single) {
				setSelectedBoxes([i]);
				return;
			}
		},
		selectBoxes = function (i: number[]) {
			if (selectMode !== SelectMode.Multi) {
				setSelectedBoxes(i);
				return;
			}
			let cp: number[] = [];
			if (i.length > 0) {
				cp = [...selectedBoxes];
				for (let x = 0; x < i.length; x++) {
					if (!selectedBoxes.includes(i[x])) cp.push(i[x]);
				}
			}
			setSelectedBoxes(cp);
		},
		deselectBox = function (i: number) {
			if (selectMode !== SelectMode.Multi) return;
			const lIndex = selectedBoxes.indexOf(i);
			if (lIndex < 0) return;
			const lCp = [...selectedBoxes];
			lCp.splice(lIndex, 1);
			setSelectedBoxes(lCp);
		},
		updateModelBox = (bpo: ModelBox, index: number) => {
			const newModels: ModelBox[] = [...modelBoxes];
			const { x, y } = newModels[index].position;
			const { x: nx, y: ny } = bpo.position;

			const dx = nx - x;
			const dy = ny - y;

			if (selectedBoxes.length > 1) {
				selectedBoxes.forEach(i => {
					if (i === index) return;
					newModels[i].position.x += dx;
					newModels[i].position.y += dy;
				});
			}
			newModels[index] = bpo;
			setModelBoxes(newModels);
			selectBox(index);
		},
		lCheckIsRightButtonEnabled = function () {
			if (!summaryScreen) return modelBoxes.length > 0;
			for (let i = 0; i < allPallets.length; i++) {
				const lPallet = allPallets[i];
				if (lPallet.Layouts.length > 0) return true;
			}
			return false;
		},
		RightButton: ButtonProps = {
			name: summaryScreen ? 'Create a Stack' : 'Save',
			restrictionCheck: function (): string | undefined {
				const colliders = getCollidersFromBoxes(
					modelBoxes,
					allBoxes[currentBoxIndex].dimensions,
					allPallets[currentPalletIndex].Dimensions,
					lGapDistance
				);
				if (detectAnyCollision(colliders)) {
					return 'Cannot save layout with overlapping boxes.';
				}
				if (
					areManyBoxesWithinBounds(
						editingLayout,
						allPallets[currentPalletIndex],
						modelBoxes,
						allBoxes
					)
				)
					return 'Cannot save layout with boxes outside of bounds.';

				if (!layoutNameUnique()) return 'Layout name must be unique.';
				return undefined;
			},
			action: () => {
				if (summaryScreen) {
					// apply the spin calculation.
					handleNext();
					return;
				}
				if (0 === modelBoxes.length) return;
				let h = 0;
				let goodBoxes: ModelBox[] = [];
				modelBoxes.forEach((bpo: ModelBox, _: number) => {
					goodBoxes.push(bpo);
					const box = allBoxes[bpo.index];
					if (box.dimensions.height > h) h = box.dimensions.height;
				});
				let newLayout = {
					...editingLayout,
					boxPositions: goodBoxes,
					height: h,
				} as Layout_t;

				if (LOG_LABEL_SPINS) {
					newLayout.boxPositions
						.sort(function (pA, pB) {
							if (pA.position.y !== pB.position.y)
								return pA.position.y - pB.position.y;
							return pA.position.x - pB.position.x;
						})
						.map(function (pBox) {
							console.log(pBox.position, 'rot', pBox.rotated, 'spin', pBox.spin);
						});
				}
				const rawName = editingLayout.name;
				const boxName = allBoxes[newLayout.boxPositions[0].index].name;

				if (/^Auto Layout \d+$/.test(rawName)) {
					const m = /^Auto Layout (\d+)$/.exec(rawName)!;
					newLayout.name = `Auto Layout ${m[1]} - ${boxName}`;
				} else if (/^Layout \d+$/.test(rawName)) {
					const m = /^Layout (\d+)$/.exec(rawName)!;
					newLayout.name = `Layout ${m[1]} - ${boxName}`;
				}

				let newPallets: Pallet_t[] = [...allPallets];
				if (allPallets[currentPalletIndex].Layouts.length > currentLayoutIndex)
					newPallets[currentPalletIndex].Layouts[currentLayoutIndex] = newLayout;
				else newPallets[currentPalletIndex].Layouts.push(newLayout);

				newLayout.box_id = allBoxes[newLayout.boxPositions[0].index].id;

				setEditingLayout(defaultLayout(layoutCount + 2, lDefaultGapDistance));
				setPallets(newPallets);
				setSummaryScreen(true);
				setSelectedBoxes([]);
				setSelectMode(SelectMode.Single);
			},
			enabled: lCheckIsRightButtonEnabled(),
		},
		startEdit = (palletIndex: number, layoutIndex: number) => () => {
			// Handle Setting Up the Boxes here.
			setCurrentPalletIndex(palletIndex);
			setCurrentLayoutIndex(layoutIndex);
			let lCurrentBoxIndex = 0;
			setModelBoxes(
				allPallets[palletIndex].Layouts[layoutIndex].boxPositions.map(
					(bpo: ModelBox) => {
						lCurrentBoxIndex = bpo.index;
						return { ...bpo, selected: false } as ModelBox;
					}
				)
			);
			setCurrentBoxIndex(lCurrentBoxIndex);
			setEditingLayout(allPallets[palletIndex].Layouts[layoutIndex]);
			setSelectedBoxes([]);
			setSelectMode(SelectMode.Single);
			setSummaryScreen(false);
		},
		getTotalLayouts = function () {
			let count = 0;
			allPallets.forEach((p: Pallet_t) => (count += p.Layouts.length));
			return count;
		},
		clearOutBoxes = function () {
			setSelectedBoxes([]);
			setModelBoxes([]);
		},
		newLayout = function () {
			let lc = getTotalLayouts();
			clearOutBoxes();
			setCurrentLayoutIndex(lc + 1);
			setCurrentPalletIndex(0);
			setEditingLayout(defaultLayout(lc + 1, lDefaultGapDistance));
			setSummaryScreen(false);
		},
		updateLayout = function (l: Layout_t, pi: number) {
			setSelectedBoxes([]);
			setSelectMode(SelectMode.Single);
			setCurrentPalletIndex(pi);
			setModelBoxes(l.boxPositions);
			setEditingLayout(l);
			setShowAuto(false);
		};

	let instruction: string;
	const add_box = function (
			box_index: number,
			rotated: boolean,
			multipick: MultiPickType,
			multipickAlongWidth: boolean
		) {
			let isRotated = rotated;
			let index = box_index;
			let modelData = getModelData(allPallets[currentPalletIndex], lSize, 0);
			let px = modelData.topX + lGapDistance / 2;
			let py = modelData.topY + lGapDistance / 2;
			let [fracX, fracY] = getFractionalCoordinates(
				modelData,
				lSize,
				lSize,
				px,
				py
			);
			let bpo: ModelBox = {
				position: {
					x: fracX,
					y: fracY,
				},
				rotated: isRotated,
				spin: true,
				index, // The box index.
				multipick: multipick,
				multipickAlongWidth: multipickAlongWidth,
			};
			setModelBoxes([...modelBoxes, bpo]);
		},
		handleBoxSelect = function (n: number) {
			if (n === currentBoxIndex) return;
			clearOutBoxes();
			setCurrentBoxIndex(n);
		},
		handlePalletSelect = function (n: number) {
			if (n === currentPalletIndex) return;
			clearOutBoxes();
			setCurrentPalletIndex(n);
		},
		editorSelectSingle = function () {
			setSelectedBoxes([]);
			setSelectMode(SelectMode.Single);
		},
		editorSelectMulti = function () {
			setSelectedBoxes([]);
			setSelectMode(SelectMode.Multi);
		},
		editorAddBox = useDebounce(function () {
			if (showAuto) return;
			editorSelectSingle();
			add_box(currentBoxIndex, false, MultiPickType.None, false);
		}, 250),
		editorAddMultiPickBox = function (pMultiPickData: MultiPickData) {
			if (showAuto) return;
			editorSelectSingle();
			add_box(
				currentBoxIndex,
				false,
				pMultiPickData.multipick,
				pMultiPickData.alongWidth
			);
		},
		editorCloneBoxes = function () {
			let boxes = selectedBoxes.map((i: number) => {
				let bpo = modelBoxes[i];
				return {
					...bpo,
					position: {
						x: bpo.position.x + 0.1,
						y: bpo.position.y + 0.1,
					},
				};
			});
			setModelBoxes([...modelBoxes, ...boxes]);
			let newBoxIndexes = boxes.map((_, i) => modelBoxes.length + i);
			setSelectedBoxes(newBoxIndexes);
			setMoveBoxes(true);
		},
		deleteLayout = (palletIndex: number, layoutIndex: number) => () =>
			lTriggerConfirm(() => {
				let newPallets = [...allPallets];
				let newLayouts = [...allPallets[palletIndex].Layouts];
				newLayouts.splice(layoutIndex, 1);
				newPallets[palletIndex].Layouts = newLayouts;
				// Shift layout indices.
				newPallets[palletIndex].Stack = newPallets[palletIndex].Stack.filter(
					(li: StackObject) => {
						return !li.layers
							.map(function (pLayer_t: Layer_t) {
								return pLayer_t.index;
							})
							.includes(layoutIndex);
					}
				).map((li: StackObject) => {
					// adjust layout indices to reflect deleted layout.
					li.layers = li.layers.map((oldLayout: Layer_t) => {
						if (oldLayout.index >= layoutIndex) oldLayout.index -= 1;
						return oldLayout;
					});
					return li;
				});
				setPallets(newPallets);
			}, `Are you sure you want to delete ${allPallets[palletIndex].Layouts[layoutIndex].name}?`);

	if (summaryScreen) {
		instruction = 'Create And Edit Layouts';
		const AddButton: ButtonProps = {
			name: 'Add new layout',
			action: newLayout,
		};

		const contentItemProps: ContentItemProps = {
			instruction,
			instructionNumber,
			LeftButton,
			RightButton,
			AddButton,
			sub_step: 1,
			sub_step_count: 3,
			isSummary: true,
		};

		contentItemProps.confirmAction = lConfirmProps;

		const lItemIterator: ItemIterator = {
			pGet: function (
				pStartIndex: number,
				pEndOffset: number,
				pSearchFilter: string | undefined
			) {
				let lLayouts = [...lAllLayoutPairs];
				if (pSearchFilter) {
					const lRegExp = new RegExp(pSearchFilter, 'i');
					lLayouts = lLayouts.filter(function (pLayoutPair: _LayoutPair) {
						const lPallet = allPallets[pLayoutPair.PalletIndex],
							lLayout = lPallet.Layouts[pLayoutPair.LayoutIndex],
							lFirstBox = allBoxes[lLayout.boxPositions[0].index];
						return (
							lRegExp.test(lLayout.name) ||
							lRegExp.test(lPallet.name) ||
							lRegExp.test(lFirstBox.name)
						);
					});
				}
				const lItemCount = lLayouts.length;
				return [
					lLayouts
						.splice(pStartIndex, pEndOffset)
						.map(function (pLayoutPair: _LayoutPair, pIndex: number) {
							const lPallet = allPallets[pLayoutPair.PalletIndex];
							const lLayout = lPallet.Layouts[pLayoutPair.LayoutIndex];
							const lLayoutProps: LayoutCellProps = {
								pallet: lPallet,
								layout: lLayout,
								startEdit: startEdit(pLayoutPair.PalletIndex, pLayoutPair.LayoutIndex),
								deleteLayout: deleteLayout(
									pLayoutPair.PalletIndex,
									pLayoutPair.LayoutIndex
								),
								duplicateLayout: duplicateConfiguration(
									pLayoutPair.LayoutIndex,
									lPallet.Layouts,
									(layouts: Layout_t[]) => {
										const newPallets = [...allPallets];
										newPallets[pLayoutPair.PalletIndex].Layouts = layouts;
										setPallets(newPallets);
									}
								),
								allBoxes,
								unitsSystem: unitsSystem,
							};
							return <LayoutCell key={pIndex} {...lLayoutProps} />;
						}),
					lItemCount,
				];
			},
			triggerKeyboard: triggerKeyboard,
		};
		const summaryProps = {
			...lItemIterator,
			instruction,
			AddButton,
			RightFooterButton: RightButton,
			LeftFooterButton: LeftButton,
			ConfirmFooter: lConfirmProps,
		};
		return (
			<div className="full-size flex-center align-center">
				<SummaryScroll {...summaryProps} />
			</div>
		);
	} else {
		instruction = 'Drag And Drop Boxes To Create A Layout';
		const contentItemProps: ContentItemProps = {
				instructionNumber,
				instruction,
				LeftButton,
				RightButton,
				sub_step: 0,
				sub_step_count: 0,
			},
			layoutModelProps: LayoutModelProps = {
				boxes: modelBoxes,
				updateModelBox: updateModelBox,
				pallet: allPallets[currentPalletIndex],
				size: lSize,
				enableDrag: selectMode !== SelectMode.Multi || moveBoxes,
				showName: true,
				allBoxes,
				selectedBoxIndices: selectedBoxes,
				selectMode,
				selectBoxes,
				deselectBox,
				gapDistance: lGapDistance,
				snapStrength,
				unitsSystem: unitsSystem,
				overhang: isNaN(editingLayout.overhang) ? 0 : editingLayout.overhang,
				moveBoxes,
			},
			autoLayoutProps: AutoLayoutProps = {
				allBoxes,
				allPallets,
				triggerSelect,
				modelSize: lSize,
				gapDistance: lGapDistance,
				boxIndex: currentBoxIndex,
				palletIndex: currentPalletIndex,
				name: 'test',
				updateLayout,
				cancelAutoLayout: () => setShowAuto(false),
				currentLayoutName: editingLayout.name,
				packFunc: (function () {
					const lApi = ApiFactory();
					return lApi.Pack.pack.bind(lApi.Pack);
				})(),
				layoutId: editingLayout.id,
				overhang: editingLayout.overhang,
			},
			getSelectedBoxes = (): number[] => {
				if (modelBoxes.length == 0) return [];
				if (selectedBoxes.length > 0) return selectedBoxes;
				return [modelBoxes.length - 1];
			},
			toggleEditorSelect = function () {
				if (selectMode === SelectMode.Multi) {
					setMoveBoxes(false);
					editorSelectSingle();
				} else {
					setMoveBoxes(false);
					editorSelectMulti();
				}
			},
			editorDeleteBox = function () {
				if (showAuto) return;
				const selected = getSelectedBoxes();
				const cp = modelBoxes
					.filter((_: ModelBox, i: number) => selected.indexOf(i) < 0)
					.map(b => b);
				setSelectedBoxes([]);
				setModelBoxes(cp);
			},
			editorClearBoxes = function () {
				if (showAuto) return;
				setSelectedBoxes([]);
				setModelBoxes([]);
			},
			editorCenter = function () {
				if (showAuto) return;
				let modelData = getModelData(
					layoutModelProps.pallet,
					layoutModelProps.size,
					0
				);
				let { w, l, norm, scale, size } = modelData,
					scale_factor = (size * scale) / norm,
					pallet_dims = { width: w, length: l } as PlaneDimensions,
					{ x: cx, y: cy } = getBoxPositionsBoundingBoxCenter(
						allBoxes,
						modelBoxes,
						scale_factor,
						pallet_dims
					),
					dx = cx - 1 / 2,
					dy = cy - 1 / 2,
					cp = modelBoxes.map((bpo: ModelBox) => {
						bpo.position.x -= dx;
						bpo.position.y -= dy;
						return bpo;
					});
				setModelBoxes(cp);
			},
			editorRotateBox = function () {
				if (showAuto) return;

				const selected = getSelectedBoxes();
				if (selected.length === 0) return;

				// Copy the model boxes so we can mutate safely
				const cp = [...modelBoxes];

				const palletWidthMM = layoutModelProps.pallet.Dimensions.width;
				const palletLengthMM = layoutModelProps.pallet.Dimensions.length;

				// 1. Compute group center in mm (same as before)
				let minXmm = Infinity,
					maxXmm = -Infinity;
				let minYmm = Infinity,
					maxYmm = -Infinity;

				selected.forEach(i => {
					const b = cp[i];
					const box = allBoxes[b.index];

					const { width: oldW, length: oldL } = getEffectiveBoxDimensions(b, box);
					const cornerXmm = b.position.x * palletWidthMM;
					const cornerYmm = b.position.y * palletLengthMM;

					const centerXmm = cornerXmm + oldW / 2;
					const centerYmm = cornerYmm + oldL / 2;

					if (centerXmm < minXmm) minXmm = centerXmm;
					if (centerXmm > maxXmm) maxXmm = centerXmm;
					if (centerYmm < minYmm) minYmm = centerYmm;
					if (centerYmm > maxYmm) maxYmm = centerYmm;
				});

				const groupCenterXmm = (minXmm + maxXmm) / 2;
				const groupCenterYmm = (minYmm + maxYmm) / 2;

				// 2. For each selected box, rotate around group center
				selected.forEach(i => {
					const b = cp[i];
					const box = allBoxes[b.index];

					// Compute old width/length in mm
					const { width: oldW, length: oldL } = getEffectiveBoxDimensions(b, box);

					// Old center in mm
					const cornerXmm = b.position.x * palletWidthMM;
					const cornerYmm = b.position.y * palletLengthMM;
					const oldCenterXmm = cornerXmm + oldW / 2;
					const oldCenterYmm = cornerYmm + oldL / 2;

					// Translate so group center is origin, do 90° rotation
					const relX = oldCenterXmm - groupCenterXmm;
					const relY = oldCenterYmm - groupCenterYmm;
					// 90° rotation: (x, y) -> (-y, x)
					const rotatedX = -relY;
					const rotatedY = relX;
					const newCenterXmm = rotatedX + groupCenterXmm;
					const newCenterYmm = rotatedY + groupCenterYmm;

					const toggledBox = rotateBox90Degrees(b);
					b.rotated = toggledBox.rotated;
					b.spin = toggledBox.spin;

					// Now that the box is "rotated," compute new dimension
					const { width: newW, length: newL } = getEffectiveBoxDimensions(b, box);

					// Re-center the box
					const newCornerXmm = newCenterXmm - newW / 2;
					const newCornerYmm = newCenterYmm - newL / 2;

					// Convert back to fraction
					b.position.x = newCornerXmm / palletWidthMM;
					b.position.y = newCornerYmm / palletLengthMM;
				});

				setModelBoxes(cp);
			},
			editorHeaderProps: EditorHeaderProps = {
				allBoxes,
				allPallets,
				showAuto,
				modelBoxes,
				currentBoxIndex,
				currentPalletIndex,
				editingLayout,
				handleBoxSelect,
				handlePalletSelect,
				setEditingLayout,
				triggerKeyboard,
				triggerSelect,
				gapDistance: lGapDistance,
				unitsSystem: unitsSystem,
				snapStrength,
				setSnapStrength,
				pSetGapDistance: lSetGapDistance,
				isNewLayout,
			},
			editorBarProps: EditorBarProps = {
				showAuto,
				editorAddBox,
				editorAddMultiPickBox,
				editorCenter,
				editorClearBoxes,
				editorDeleteBox,
				editorRotateBox,
				selectMode,
				toggleEditorSelect,
				setShowAuto,
				toggleMoveBoxes,
				editorCloneBoxes,
				moveBoxes,
			};
		return (
			<ContentItem {...contentItemProps}>
				<div className="full-size grid-col-70">
					<div className="full-height full-width grid-row-10-70">
						<div className="full-size flex-col margin-top">
							<ContentFooter
								RightButton={RightButton}
								LeftButton={LeftButton}
								confirmAction={lConfirmProps}
							/>
						</div>

						<div
							className="full-size double-padding-left double-padding-top flex-center align-center"
							ref={lContainerRef}
						>
							{showAuto ? (
								<AutoLayout {...autoLayoutProps} />
							) : lContainerRect ? (
								<LayoutModel {...layoutModelProps} />
							) : null}
						</div>

						<div className="full-size flex-col flex-end align-center">
							<div className="full-width margin-bottom flex-center">
								<EditorBar {...editorBarProps} />
							</div>
						</div>
					</div>
					<div className="full-size border-left color-blue">
						<EditorHeader {...editorHeaderProps} />
					</div>
				</div>
			</ContentItem>
		);
	}
}
